/**
 * Hono application factory.
 *
 * Builds a `Hono<NexusEnv>` app with the standard middleware chain:
 * OTel → secure headers → CORS → request ID → body limit → routes.
 *
 * Module routes are NOT mounted here yet — `setupModuleRoutes` (from
 * `./routes/module-routes.js`) is invoked by the server lifecycle after module
 * registration. In F2 foundation this file only registers health / ready /
 * OpenAPI stub endpoints.
 */

import { OpenAPIHono } from '@hono/zod-openapi'
import { swaggerUI } from '@hono/swagger-ui'
import { cors } from 'hono/cors'
import { secureHeaders } from 'hono/secure-headers'
import { compress } from 'hono/compress'
import { bodyLimit } from 'hono/body-limit'
import { httpInstrumentationMiddleware as otel } from '@hono/otel'

import pkg from '../../package.json' with { type: 'json' }
import { env, getConfig } from '@kernel/config/env.js'
import { logger } from '@kernel/logger/index.js'
import { getInstanceInfo } from '@kernel/config/instance-id.js'
import { buildEffectiveCorsOrigins } from '../core/utils/cors.js'

/** OTel enabled if OTEL_ENABLED env var is truthy. Avoids pulling module config into the HTTP layer. */
function isOtelEnabled(): boolean {
  const v = process.env['OTEL_ENABLED']
  return v === '1' || v === 'true'
}
import type { SpaEntry, ServeSPAFunction } from '@kernel/config/types.js'

import type { HonoApp } from './types.js'
import type { NexusEnv } from './types.js'
import { isShuttingDown } from './lifecycle-state.js'
import { requestIdMiddleware } from './middleware/request-id.middleware.js'
import { errorHandler, createErrorResponse } from './middleware/error.middleware.js'
import { shutdownMiddleware } from './middleware/shutdown.middleware.js'
import { setupModuleRoutes } from './routes/module-routes.js'
import { serveSPA } from './spa-handler.js'
import { buildEntitiesOpenApi } from './openapi/index.js'
import { getOrderedModulesInternal } from '@kernel/engine/index.js'

/** HTTP JSON body size cap — same as legacy Express value. */
const BODY_LIMIT_BYTES = 1 * 1024 * 1024

export interface CreateAppOptions {
  /** Custom routes to mount before module routes. */
  beforeRoutes?: (app: HonoApp, serveSPAFn: ServeSPAFunction) => void | Promise<void>
  /** Custom routes to mount after module routes. */
  afterRoutes?: (app: HonoApp, serveSPAFn: ServeSPAFunction) => void | Promise<void>
  /** Declarative SPA entries (served + CORS). Passed from StartOptions.spas. */
  spas?: SpaEntry[]
  /**
   * Pre-created http.Server for Vite HMR WebSocket sharing.
   * Must be created before createApp() so Vite binds its upgrade handler to it.
   */
  httpServer?: import('node:http').Server
}

export async function createApp(options: CreateAppOptions = {}): Promise<HonoApp> {
  const app = new OpenAPIHono<NexusEnv>()

  // OpenTelemetry middleware (before anything so all requests are traced).
  if (isOtelEnabled()) {
    app.use('*', otel())
  }

  // Security headers (equivalent to helmet).
  // Hono's secureHeaders doesn't enforce CSP unless `contentSecurityPolicy` is
  // provided — mirrors the legacy behaviour where dev ran without CSP and prod
  // kept helmet's defaults.
  app.use('*', secureHeaders())

  // CORS — merge env CORS_ORIGIN with any origins declared in spas[].
  const effectiveCorsOrigins = buildEffectiveCorsOrigins(env.CORS_ORIGIN, options.spas)
  app.use(
    '*',
    cors({
      origin: (origin) => {
        if (effectiveCorsOrigins === true || effectiveCorsOrigins === '*') return origin ?? '*'
        if (Array.isArray(effectiveCorsOrigins)) {
          return effectiveCorsOrigins.includes(origin) ? origin : ''
        }
        return effectiveCorsOrigins === origin ? origin : ''
      },
      credentials: true,
      allowHeaders: [
        'Content-Type',
        'Authorization',
        'X-Correlation-ID',
        'X-Request-ID',
        'X-Nexus-Client'
      ],
      exposeHeaders: ['X-Correlation-ID', 'X-Request-ID']
    })
  )

  // Request / correlation ID (sets c.get('requestId')).
  app.use('*', requestIdMiddleware)

  // Tag responses with `Connection: close` when graceful shutdown is active,
  // so keep-alive clients drop their sockets after the current request.
  app.use('*', shutdownMiddleware)

  // Early Content-Length rejection — avoids EPIPE in HTTP/1.1 by responding before
  // reading the body. When Content-Length > limit the server responds with 413 and
  // keeps the connection alive (keep-alive), so the client receives the status code
  // cleanly instead of a broken-pipe error.
  app.use('*', async (c, next) => {
    const cl = c.req.header('content-length')
    if (cl !== null && cl !== undefined) {
      const size = parseInt(cl, 10)
      if (!isNaN(size) && size > BODY_LIMIT_BYTES) {
        return c.json(
          createErrorResponse(
            'STORAGE_PAYLOAD_TOO_LARGE',
            'Payload too large: Request body exceeds the allowed limit (1MB)',
            undefined,
            undefined,
            c.get('requestId')
          ),
          413
        )
      }
    }
    await next()
  })

  // Body size cap — fallback for chunked transfers without Content-Length.
  app.use(
    '*',
    bodyLimit({
      maxSize: BODY_LIMIT_BYTES,
      onError: (c) =>
        c.json(
          createErrorResponse(
            'STORAGE_PAYLOAD_TOO_LARGE',
            'Payload too large: Request body exceeds the allowed limit (1MB)',
            undefined,
            undefined,
            c.get('requestId')
          ),
          413
        )
    })
  )

  // Compression (applied to responses >= 1KB).
  app.use('*', compress())

  // Optional debug logging at API boundary.
  const logLevel = process.env['LOG_LEVEL']
  if (logLevel === 'debug' || logLevel === 'trace') {
    app.use('*', async (c, next) => {
      const start = Date.now()
      await next()
      const ms = Date.now() - start
      const logData = {
        reqId: c.get('correlationId')?.slice(0, 8) ?? 'unknown',
        method: c.req.method,
        url: c.req.url,
        status: c.res.status,
        ms
      }
      if (c.req.path.startsWith('/api/')) {
        logger.debug(logData, `${c.req.method} ${c.req.path} ${c.res.status} ${ms}ms`)
      } else {
        logger.trace(logData, `${c.req.method} ${c.req.path} ${c.res.status} ${ms}ms`)
      }
    })
  }

  // beforeRoutes hook.
  if (options.beforeRoutes) {
    await options.beforeRoutes(app, (endpoint, distPath, opts) => serveSPA(app, [{ endpoint, path: distPath, ...opts }]))
  }

  // Module routes (auto-discovered from registered modules and plugins).
  await setupModuleRoutes(app)

  // Liveness probe.
  app.get('/api/health', (c) => {
    const instance = getInstanceInfo()
    return c.json({
      status: 'ok',
      version: pkg.version,
      instanceId: instance?.instanceId ?? null,
      timestamp: new Date().toISOString()
    })
  })

  // Readiness probe.
  app.get('/api/ready', async (c) => {
    // During graceful shutdown: short-circuit with 503 so load balancers stop
    // routing new traffic to this instance immediately, without waiting for
    // the health registry timeout.
    if (isShuttingDown()) {
      return c.json(
        { status: 'shutting_down', checks: {}, timestamp: new Date().toISOString() },
        503
      )
    }
    try {
      const { getHealthRegistry } = await import(
        '../modules/observability/observability.service.js'
      )
      const registry = getHealthRegistry()
      const result = await registry.checkAll()
      return c.json(result, result.status === 'ok' ? 200 : 503)
    } catch {
      return c.json(
        { status: 'error', checks: {}, timestamp: new Date().toISOString() },
        503
      )
    }
  })

  // OpenAPI spec — merges manual routes (app.openapi()) with auto-generated
  // entity/action routes walked from the module store. Cached after first
  // request since modules don't change at runtime.
  const docConfig = {
    openapi: '3.0.0' as const,
    info: {
      title: 'Nexus API',
      version: pkg.version,
      description: 'Nexus BaaS REST API'
    },
    servers: [{ url: '/' }]
  }

  let cachedSpec: Record<string, unknown> | null = null

  app.get('/api/openapi.json', (c) => {
    if (!cachedSpec) {
      const base = app.getOpenAPIDocument(docConfig) as unknown as Record<string, unknown>
      const entitySpec = buildEntitiesOpenApi(getOrderedModulesInternal())
      cachedSpec = {
        ...base,
        // Manual routes win — they carry proper Zod schemas via app.openapi().
        paths: {
          ...entitySpec.paths,
          ...((base['paths'] as Record<string, unknown> | undefined) ?? {})
        }
      }
    }
    return c.json(cachedSpec)
  })

  // Swagger UI at /api/docs.
  app.get('/api/docs', swaggerUI({ url: '/api/openapi.json' }))

  // afterRoutes hook.
  if (options.afterRoutes) {
    await options.afterRoutes(app, (endpoint, distPath, opts) => serveSPA(app, [{ endpoint, path: distPath, ...opts }]))
  }

  const spaOpts = options.httpServer ? { httpServer: options.httpServer } : {}

  // SPA handler — mount static files and index.html fallback for each SpaEntry.
  // Runs after all API routes so /api/* is never intercepted by the SPA.
  await serveSPA(app, options.spas, spaOpts)

  // Built-in UI SPA (Vite HMR in dev, static files in prod).
  // Skipped if the user already registered an SPA on the same endpoint via options.spas.
  const { ui } = getConfig()
  if (ui.enabled) {
    await serveSPA(app, [{ endpoint: ui.base, path: ui.path, viteSrc: '../ui' }], spaOpts)
  }

  // API 404 handler — returns JSON for unmatched /api/* routes.
  app.notFound((c) => {
    if (c.req.path.startsWith('/api/')) {
      return c.json(
        createErrorResponse('NOT_FOUND', 'API endpoint not found', undefined, undefined, c.get('requestId')),
        404
      )
    }
    // Non-API 404: let SPA handler (mounted later) take over, or return plain text.
    return c.text('Not Found', 404)
  })

  // Global error handler.
  app.onError(errorHandler)

  return app
}
